import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/utils';

const badgeVariants = cva(
  'tov:inline-flex tov:w-fit tov:shrink-0 tov:items-center tov:justify-center tov:gap-1 tov:overflow-hidden tov:rounded-full tov:border tov:border-transparent tov:px-2 tov:py-0.5 tov:text-xs tov:font-medium tov:whitespace-nowrap tov:transition-[color,box-shadow] tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50 tov:aria-invalid:border-destructive tov:aria-invalid:ring-destructive/20 tov:dark:aria-invalid:ring-destructive/40 tov:[&>svg]:pointer-events-none tov:[&>svg]:size-3',
  {
    variants: {
      variant: {
        default:
          'tov:bg-primary tov:text-primary-foreground tov:[a&]:hover:bg-primary/90',
        secondary:
          'tov:bg-secondary tov:text-secondary-foreground tov:[a&]:hover:bg-secondary/90',
        destructive:
          'tov:bg-destructive tov:text-white tov:focus-visible:ring-destructive/20 tov:dark:bg-destructive/60 tov:dark:focus-visible:ring-destructive/40 tov:[a&]:hover:bg-destructive/90',
        outline:
          'tov:border-border tov:text-foreground tov:[a&]:hover:bg-accent tov:[a&]:hover:text-accent-foreground',
        ghost: 'tov:[a&]:hover:bg-accent tov:[a&]:hover:text-accent-foreground',
        link: 'tov:text-primary tov:underline-offset-4 tov:[a&]:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';
  const designSystemVariantClass =
    variant === 'default' ||
    variant === 'secondary' ||
    variant === 'outline' ||
    variant === 'destructive'
      ? `tov-badge--${variant}`
      : null;

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(
        'tov-badge',
        designSystemVariantClass,
        badgeVariants({ variant }),
        className,
      )}
      {...props}
    />
  );
}

interface BadgeAnchorProps
  extends
    Omit<React.ComponentProps<'span'>, 'content'>,
    VariantProps<typeof badgeVariants> {
  badge: React.ReactNode;
  badgeClassName?: string;
  children: React.ReactNode;
  show?: boolean;
}

function BadgeAnchor({
  badge,
  badgeClassName,
  children,
  className,
  show = true,
  variant = 'default',
  ...props
}: BadgeAnchorProps) {
  return (
    <span
      data-slot="badge-anchor"
      className={cn('tov-badge-anchor', className)}
      {...props}
    >
      {children}
      {show ? (
        <Badge
          className={cn('tov-badge-anchor__badge', badgeClassName)}
          variant={variant}
        >
          {badge}
        </Badge>
      ) : null}
    </span>
  );
}

export { Badge, BadgeAnchor, badgeVariants };
